import { create } from "zustand";
import { registerOrgScopedReset } from '@/lib/org-scope';
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { sanitizeArtifactLabel, sanitizeArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";
import type { ValidationProgress } from "@/services/multi-agent/validationProgress";
/*
 * `cleanDisplayIntent` moved to `services/multi-agent/transcript.ts` (2026-09) — imported here for
 * this store's own two call sites below, unchanged otherwise. It moved because the Weave v1 surface
 * needed the SAME rule and had never gotten it (`withUserTurn` rendered the raw, unfiltered intent
 * for both the live run and every archived one) — one shared implementation is what keeps the
 * legacy and v1 surfaces from disagreeing about what a reader sees for the same stored intent. See
 * that file for the full "why" — the leaked internal id/metadata-type/code dump this fixes there,
 * this store already avoided.
 */
import { cleanDisplayIntent } from "@/services/multi-agent/transcript";

export type NodeStatus =
  | "planned"
  | "ready"
  | "running"
  | "tool_calling"
  | "done"
  // Terminal-but-partial: the node finished producing an answer but flagged
  // gaps (backend emits this from reactive_worker.py / config_planner.py).
  // Rendered as an amber dot, not the grey "unstarted" fallback — GUI_SYNC_SPEC
  // 2026-08-29 P0-1.
  | "completed_with_gaps"
  | "failed"
  | "skipped";

export interface AgentNodeData {
  [key: string]: unknown;
  id: string;
  role: string;
  task: string;
  status: NodeStatus;
  toolInFlight?: string;
  output?: { summary?: string };
  isSupervisor?: boolean;
}

export interface PlanEdge {
  from: string;
  to: string;
  active?: boolean;
}

/** Lightweight attachment shape carried on a sent user message so the chat
 *  bubble can render inline thumbnails / chips (image preview, file icon+name).
 *  Holds only what the bubble needs — the in-memory client thumbnail, not the
 *  base64 payload — so it stays small and never bloats persisted state. */
export interface ChatMsgAttachment {
  id: string;
  name: string;
  kind: string;                // image | pdf | audio | url | text | code
  thumbnailDataUrl?: string;   // images only — in-memory client preview
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  reasoning?: string;          // muted italic — Anthropic extended thinking
  streaming?: boolean;
  attachments?: ChatMsgAttachment[]; // sent user message → inline bubble thumbnails (#11)
}

export interface TodoItem {
  id: string;
  label: string;
  status: "queued" | "running" | "done" | "failed";
}

/** One `changeset_phases` row / `changeset_phase_started`|`changeset_phase_finished`
 *  SSE frame shape, off `app/routers/changeset.py`'s `_make_listener`. `status` is
 *  "running" while in flight (started frame carries none — the reducer stamps it),
 *  then one of PhaseStatus's terminal values ("succeeded" | "failed" | "skipped")
 *  on the finished frame. The fixed 4-phase order is mdapi_check -> mdapi_deploy ->
 *  dml_grants -> verify (`ord` 0-3). */
export interface ChangesetPhase {
  ord: number;
  phase: string;
  status: string;
  counts?: Record<string, number>;
  error?: unknown;
}

/** The changeset HITL flow's state machine (GUI_SYNC_SPEC_2026-08-29 P0-3 / §3).
 *  `phase` here is the CARD's phase (ready/prechecking/…), distinct from the
 *  `phases[]` array's per-apply-phase (mdapi_check/…) entries — same word, two
 *  different axes, kept as separate fields to avoid confusing the two. */
export interface ChangesetState {
  runId: string;
  phase:
    | "ready"
    | "prechecking"
    | "precheck_result"
    | "applying"
    | "applied"
    | "failed"
    | "reverting"
    | "reverted";
  applyLogId: string | null;
  // POST /changeset/precheck response (§3 state 2/3). No phases[] here — see
  // the interface's own note on the spec's "no real phase status pre-apply" caveat.
  precheck: {
    passed: boolean;
    mdapi: Record<string, unknown>;
    grants: Record<string, unknown>;
    totals: Record<string, number>;
    warnings: unknown[];
  } | null;
  // Requires an override_reason to Apply anyway once precheck.passed === false.
  overrideReason: string | null;
  // Live per-apply-phase progress driven off changeset_phase_started/finished
  // SSE frames during POST /changeset/apply or /revert (§3 state 4).
  phases: ChangesetPhase[];
  // POST /changeset/apply response (§3 state 5).
  applyResult: {
    applied: boolean;
    outcome: string; // "succeeded" | "failed" | "deployed_grants_failed" (etc — see outcome.value)
    state: string;
    revertAvailable: boolean;
    warnings: unknown[];
  } | null;
  // POST /changeset/revert response (§3 state 6).
  revertResult: {
    reverted: boolean;
    outcome: string;
    undeployable: unknown[];
  } | null;
  error: string | null;
}

/** One option in a pending interaction's `options[]` (server caps at 4) —
 *  mirrors the MSA `interaction_required` payload's Option shape. Extra
 *  backend fields (description, recommended, badge — see the Weave v1
 *  `Option` type) are accepted on the wire but not rendered by the minimal
 *  legacy card; only what's needed to label + submit a choice is kept. */
export interface PendingInteractionOption {
  option_id: string;
  label: string;
}

/**
 * The run's single active pending interaction, for the LEGACY (default)
 * SSE stream — `useRunStream.ts`'s `interaction_required`/`_resolved`/
 * `_expired` cases, NOT the flag-gated Weave protocol v1 path (that one
 * carries the richer `Interaction` type from `services/multi-agent/protocol`
 * via `runStateStore`).
 *
 * At most one at a time: a second `interaction_required` arriving before
 * this one resolves REPLACES it — matches the backend's own "pending" ==
 * the one to lead with semantics, and keeps this a single card rather than
 * a queue. Cleared on a successful respond, on `interaction_resolved` /
 * `interaction_expired`, and implicitly by `startRun()`/`reset()` spreading
 * `_initial` for the next turn.
 */
export interface PendingInteraction {
  interaction_id: string;
  kind: string;
  prompt: string;
  detail: string | null;
  options: PendingInteractionOption[];
  allowFreeText: boolean;
  status: string;
}

export interface MultiAgentState {
  runId: string | null;
  // Client-generated identity for the CURRENT turn, independent of `runId`.
  // A resumed run (backend answers a pending clarification by continuing the
  // SAME run_id rather than minting a new one) can make two DIFFERENT chat
  // turns share one `runId`. `pushTurnToThread` dedups on `turnKey`, not
  // `runId`, so those two turns don't collide and overwrite one another.
  // Set fresh on every startRun() (a real new send); mirrors `runId` (falls
  // back to it) when a run is restored from the backend on hydrateFromDetail,
  // where no client-side turnKey exists to recover.
  turnKey: string | null;
  status:
    | "idle"
    | "starting"
    | "planning"
    | "awaiting_approval"
    | "running"
    | "done"
    | "failed"
    | "cancelled";
  lane: string | null;
  // True when the current view is a historical run loaded for reading.
  // Disables the input box, approve, and stop — clicking a past run from
  // the history list flips this on; "New run" flips it off and clears.
  readOnly: boolean;
  // Run timing (ms epoch) anchored in the store so the elapsed timer survives
  // tab-switches / re-mounts and captures the WHOLE run, not just on-page time.
  runStartedAt: number | null;
  runFinishedAt: number | null;

  chat: ChatMessage[];
  todo: TodoItem[];

  nodes: Record<string, AgentNodeData>;
  edges: PlanEdge[];

  // streaming buffers keyed by node_id
  reasoningBuffer: Record<string, string>;
  textBuffer: Record<string, string>;
  finalText: string;

  // partial findings streamed from workers as they finish
  partialFindings: Array<{ nodeId: string; role: string; summary: string }>;

  // learning lookback stats (memory_loaded event at planner start)
  memoryLoaded: { similarRuns: number; knownFacts: number; recentGaps: number } | null;

  // Prior turns/artifacts loaded from the current conversation session.
  // Backend emits this when the planner stitched follow-up context.
  sessionContext: {
    turnCount: number;
    artifactCount: number;
    badToolCount: number;
    compressed: boolean;
    compressedChars?: number;
    keptTurns?: number | null;
  } | null;

  // promotion totals at finalize
  promotionTotals: { factCount: number; artifactCount: number } | null;

  // live cost meter
  cost: { usedUsd: number; maxUsd: number; tokensIn: number; tokensOut: number; toolCalls: number } | null;

  // Live compile-gate progress (`validate_progress` SSE). Single slot: one
  // validate-save runs at a time per run; consumers gate on their own
  // in-flight flag (and className) so a stale slot can never render.
  validationProgress: ValidationProgress | null;

  // final synthesized markdown (separate from streaming buffer so we can
  // render a polished result card)
  finalMarkdown: string;
  finalArtifactId: string | null;

  // Conversation-grounded follow-up suggestions from the backend
  // (`followup_suggestions` SSE event / hydrated summary). When present the UI
  // shows these instead of the static lane templates.
  followUps: Array<{ label: string; hint: string; question: string }>;

  // recent runs history (for resume-on-mount)
  history: Array<{
    id: string;
    session_id?: string | null;
    user_intent: string;
    lane: string | null;
    status: string;
    started_at: string;
    finished_at: string | null;
    node_count: number;
    cost_usd: number;
    tokens_total: number;
  }>;

  // user feedback recorded
  feedbackRating: number | null;

  // auto-fix proposals (P12)
  proposedFixes: Array<{
    kind: string;
    // New code_patch contract fields (iter 14+). `target` retained as a
    // fallback for legacy proposals.
    file?: string;
    component_name?: string;
    metadata_type?: string;
    validation_status?: string;
    target?: string;
    risk: string;
    summary: string;
  }>;

  // verifier verdict — score 0–10, gap list, no_evidence flag (audit lane
  // had zero org tool calls). Surfaced as a tile + a safety banner so
  // the user can see HOW grounded the answer is.
  verifier: {
    ok: boolean;
    score: number;
    gaps: string[];
    noEvidence: boolean;
    replanCount: number;
  } | null;

  // Synth produced output that violated the lane contract (e.g. missing
  // diff fence on apex_change). Surfaced as a banner so the user knows
  // the answer below may be malformed even if it streamed cleanly.
  contractViolation: { lane: string; violations: string[] } | null;

  // Blocked-state gate (dev-agent parity): when the org's Salesforce metadata /
  // RAG code index isn't ready, the run is short-circuited with a message and
  // this carries the optional retrigger action (Start/Retry indexing) so the
  // answer column can render the button. null = no action (in_progress / metadata).
  blockedAction: { kind: string; label: string; endpoint: string; enabled?: boolean } | null;

  // auto_fix.propose failed — Apply path won't be available for this
  // run. Surfaced so the user knows it's not a UI bug.
  autoFixFailed: { reason: string } | null;

  // Thread of prior Q&A turns in the current chat session. Refine
  // pushes the current run into this array before starting the next,
  // so previous answers stay visible above the active run instead of
  // each follow-up wiping the screen. Cleared by "New run".
  thread: Array<{
    runId: string;
    // Dedup/lookup key for pushTurnToThread — see MultiAgentState.turnKey.
    // Distinct from `runId` so a resumed run sharing a `runId` with a prior
    // turn cannot make this entry collide with that prior turn's entry.
    turnKey: string;
    intent: string;
    attachments?: ChatMsgAttachment[]; // sent-message thumbnails for past turns (#11)
    finalMarkdown: string;
    lane: string | null;
    // Snapshot of what the agent DID this turn (timeline + reasoning
    // tail). Lets the user scroll back through prior turns and remember
    // what the agent looked at, not just what it answered.
    activity: Array<{
      id: number;
      kind: string;
      text: string;
      tone: "info" | "ok" | "warn" | "err";
      tool?: string;
      argsSummary?: string;
      durationMs?: number;
    }>;
    reasoning: string;
    artifactIds: string[];
    // The interaction still open for THIS turn when it was demoted off the
    // live slice (see MultiAgentState.pendingInteractions doc) — null when
    // the turn had none, or it already resolved before the next send. The
    // turn holds only the id; the interaction body lives in the session-level
    // `pendingInteractions` map so there is exactly one copy of the fact,
    // not one per turn that carries it.
    pendingInteractionId: string | null;
  }>;
  // The conversation session_id. Set when the first run in a chat
  // starts; persisted across follow-ups so the backend planner can
  // pull prior turns. Cleared by clearThread().
  sessionId: string | null;

  // signal that the planner couldn't produce a real plan and fell back to
  // a synthesizer-only answer — surfaced as a banner so the user knows.
  plannerFallback: { reason: string; rawPreview: string } | null;

  // signal that the planner reused a past run's plan (high similarity match)
  planReused: { fromRunId: string; similarity: number; lane: string } | null;

  // Artifacts produced during the run — each tool output that was promoted
  // to the artifact store fires an artifact_created event. The Result
  // column renders these as copyable cards so the user can pull individual
  // pieces of evidence without scrolling through the whole markdown.
  artifacts: Array<{
    id: string;
    type: string;
    name: string;
    toolName: string;
    byteSize: number;
    createdAt: number;
    // The run that PRODUCED this artifact. Lets each Q→A turn render its OWN
    // results/evidence/diff/code instead of the whole session's artifacts piling
    // up at the end of the chat. The Workspace still shows the accumulated union.
    runId?: string;
    // Typed-metadata blob for uploaded attachments (tool_name='intake'):
    // { source_kind, mime, render, title?, final_url?, page_count?, line_count?,
    //   transcript_chars? }. Present only for intake artifacts; null otherwise.
    intake?: Record<string, unknown> | null;
  }>;

  // node selected in the graph (clicked) — opens the per-agent detail drawer
  selectedNodeId: string | null;

  // Live SSE connection state — surfaced in the header pill so the user
  // knows if the stream is healthy.
  sseStatus: "idle" | "connecting" | "connected" | "disconnected" | "reconnecting";

  // Salesforce connection health for the chat pill (dev-agent parity).
  // Optimistic-true; flipped false by an auth_required / sf_token_unauthorized
  // SSE event. The pill lets the user manually reconnect (REFRESH_SALESFORCE_TOKEN).
  sfConnected: boolean;

  // Chronological activity log feeding the "Steps" column — every tool
  // call, validation, replan, approval, etc. appears here in arrival order.
  activityLog: Array<{
    id: number;                                     // _event_id
    ts: number;                                     // emitted_at_ms
    kind: string;                                   // event_type
    nodeId?: string | null;
    text: string;                                   // human-readable summary
    tone: "info" | "ok" | "warn" | "err";
    // Tool-call extras (populated for tool_call_started/completed). Lets
    // the Steps column render a proper tool pill like the dev-agent chat
    // instead of just a text line.
    tool?: string;
    argsSummary?: string;
    durationMs?: number;
    ok?: boolean;
    resultSummary?: string;
  }>;

  // The lane_picked event's `escalation_hint` (intent_router.py) — "none" |
  // "fanout" today. Carried for future escalation-aware UI; no dedicated
  // widget yet (GUI_SYNC_SPEC_2026-08-29 P0-2).
  escalationHint: string | null;

  // Changeset HITL (GUI_SYNC_SPEC_2026-08-29 P0-3) — one slice driving
  // <ChangesetCard>. null until the run has a change_plan/permission_change
  // artifact AND the card has been mounted (ready state set on mount, not on
  // run start, since not every run produces a deployable artifact).
  changeset: ChangesetState | null;

  // The legacy-stream pending interaction (clarify / approve_* question the
  // backend needs answered mid-run) — see `PendingInteraction`'s doc for why
  // this is separate from the Weave v1 `runStateStore` equivalent. null when
  // there is no open question for the current run.
  pendingInteraction: PendingInteraction | null;

  // Session-level, durable "what's still waiting" projection (F3 fix,
  // 2026-09-14) — the ONE owner of a carried ask's fact. `pendingInteraction`
  // above is only the LIVE run's single active-question slot and is reset to
  // null by every `startRun()`; without a session-level copy, `approve_changeset`
  // (and any non-blocking ask) was unconditionally destroyed the moment the
  // user sent the next message (see F3 forensics). Keyed by `interaction_id`
  // so a `PastTurn` needs only the id (see `thread[].pendingInteractionId`) —
  // never a duplicated copy of the interaction object per turn. Each entry
  // also carries the `runId` it belongs to (a carried card must respond
  // against ITS OWN run, not whatever is currently live). Survives
  // `startRun()`/`reset()` (see those actions); cleared only by
  // `resolveInteraction()` (answered/expired) or `clearThread()` (new chat).
  pendingInteractions: Record<string, PendingInteraction & { runId: string }>;

  // Mirrors POST /api/runs's optional `resumed` response field for the
  // CURRENT run — true when the backend answered a pending clarification by
  // continuing the SAME run_id (see RunCreatedOut.resumed) instead of a
  // fresh one. Not read by the thread-coalescing fix itself (turnKey handles
  // that unconditionally) — carried for callers/telemetry that want to know.
  runResumed: boolean;

  // === actions ===
  startRun: (
    runId: string,
    intent: string,
    attachments?: ChatMsgAttachment[],
    resumed?: boolean,
  ) => void;
  setStatus: (s: MultiAgentState["status"]) => void;
  setLane: (lane: string) => void;
  setBlockedAction: (action: MultiAgentState["blockedAction"]) => void;
  appendUserMessage: (text: string) => void;
  appendReasoning: (nodeId: string, text: string) => void;
  endReasoning: (nodeId: string) => void;
  appendText: (nodeId: string, text: string) => void;
  endText: (nodeId: string) => void;
  setPlan: (nodes: AgentNodeData[], edges: PlanEdge[], todo: TodoItem[]) => void;
  setNodeStatus: (nodeId: string, status: NodeStatus, toolInFlight?: string) => void;
  setNodeOutput: (nodeId: string, output: AgentNodeData["output"]) => void;
  activateEdge: (from: string, to: string) => void;
  approvePlan: () => void;
  addPartialFinding: (nodeId: string, role: string, summary: string) => void;
  setMemoryLoaded: (m: NonNullable<MultiAgentState["memoryLoaded"]>) => void;
  setSessionContext: (m: NonNullable<MultiAgentState["sessionContext"]>) => void;
  setPromotionTotals: (t: NonNullable<MultiAgentState["promotionTotals"]>) => void;
  setCost: (c: NonNullable<MultiAgentState["cost"]>) => void;
  setValidationProgress: (p: ValidationProgress | null) => void;
  setFinalArtifact: (artifactId: string | null) => void;
  setFollowUps: (f: MultiAgentState["followUps"]) => void;
  setHistory: (h: MultiAgentState["history"]) => void;
  setFeedback: (rating: number) => void;
  setProposedFixes: (fixes: MultiAgentState["proposedFixes"]) => void;
  setVerifier: (v: NonNullable<MultiAgentState["verifier"]>) => void;
  setContractViolation: (v: NonNullable<MultiAgentState["contractViolation"]>) => void;
  setAutoFixFailed: (v: NonNullable<MultiAgentState["autoFixFailed"]>) => void;
  // Snapshot the current run into the thread (called before refine).
  pushTurnToThread: () => void;
  // Clear the thread (called by "New run").
  clearThread: () => void;
  // Internal: mirror persistent fields to localStorage.
  _persistMA: () => void;
  setPlannerFallback: (f: MultiAgentState["plannerFallback"]) => void;
  setPlanReused: (p: MultiAgentState["planReused"]) => void;
  selectNode: (nodeId: string | null) => void;
  appendActivity: (entry: MultiAgentState["activityLog"][number]) => void;
  setSseStatus: (s: "idle" | "connecting" | "connected" | "disconnected" | "reconnecting") => void;
  setSfConnected: (v: boolean) => void;
  addArtifact: (a: MultiAgentState["artifacts"][number]) => void;
  removeArtifact: (id: string) => void;
  markCancelled: () => void;
  // Clean up after a normal terminal event (run_completed / run_failed
  // / budget_exceeded). Flips any still-in-flight node to a terminal
  // status and clears tool spinners so the UI doesn't keep spinning
  // after the answer is already shown.
  finalizeRun: (status: "done" | "failed") => void;
  hydrateFromDetail: (detail: {
    run: { id: string; session_id?: string | null; user_intent: string; lane: string | null; status: string; started_at?: string | null; finished_at?: string | null };
    session_id?: string | null;
    plan: { tasks: Array<{ id: string; role: string; task: string }>; edges?: [string, string][] } | null;
    todo: Array<{ id: string; label: string; status: string }>;
    nodes: Array<{ id: string; role: string; task: string; status: string; isSupervisor?: boolean; toolInFlight?: string | null }>;
    edges: [string, string][];
    chat: Array<{ role: "user" | "assistant"; text?: string; reasoning?: string }>;
    partial_findings: Array<{ nodeId: string; role: string; summary: string }>;
    proposed_fixes: Array<{ kind: string; target: string; risk: string; summary: string }>;
    final_markdown: string;
    final_artifact_id: string | null;
    follow_ups?: Array<{ label: string; hint: string; question: string }>;
    cost: { usedUsd: number; maxUsd: number; tokensIn: number; tokensOut: number; toolCalls: number } | null;
    memory_loaded: { similarRuns: number; knownFacts: number; recentGaps: number } | null;
    session_context: MultiAgentState["sessionContext"];
    session_runs?: Array<{
      runId: string;
      intent: string;
      finalMarkdown: string;
      lane: string | null;
      status: string;
      startedAt: string | null;
      finishedAt: string | null;
      artifacts?: Array<{ id: string; type: string; name: string; tool_name: string; metadata: Record<string, unknown> | null; created_at: string | null }>;
    }>;
    promotion_totals: { factCount: number; artifactCount: number } | null;
    artifacts?: Array<{ id: string; type: string; name: string; tool_name: string; metadata: Record<string, unknown> | null; created_at: string | null }>;
  }, options?: { preserveSession?: boolean; viewOnly?: boolean }) => void;
  setEscalationHint: (h: string | null) => void;
  // Changeset HITL reducer actions (GUI_SYNC_SPEC_2026-08-29 P0-3). Each maps
  // 1:1 to a §3 state transition — kept as named actions (not one generic
  // `setChangeset(partial)`) so each SSE case in useRunStream.ts reads as the
  // transition it is, and so store-level reducer tests can call them directly
  // without constructing a full partial state object by hand.
  changesetReady: (runId: string) => void;
  changesetPrechecking: () => void;
  changesetPrecheckResult: (p: NonNullable<ChangesetState["precheck"]>) => void;
  changesetSetOverrideReason: (reason: string | null) => void;
  changesetApplying: (applyLogId: string | null) => void;
  changesetReverting: () => void;
  changesetPhaseStarted: (p: { ord: number; phase: string }) => void;
  changesetPhaseFinished: (p: { ord: number; phase: string; status: string; counts?: Record<string, number>; error?: unknown }) => void;
  changesetApplied: (r: NonNullable<ChangesetState["applyResult"]> & { applyLogId: string | null; phases?: ChangesetPhase[] }) => void;
  changesetReverted: (r: NonNullable<ChangesetState["revertResult"]> & { applyLogId: string | null }) => void;
  changesetError: (message: string) => void;
  resetChangeset: () => void;
  // Legacy-stream pending interaction (see `PendingInteraction`). Set on
  // `interaction_required`; a second call before it clears REPLACES the
  // current one (single active question, not a queue).
  setPendingInteraction: (i: PendingInteraction | null) => void;
  // Clears it — called on a successful `respond`, and on
  // `interaction_resolved` / `interaction_expired`.
  clearPendingInteraction: () => void;
  // Resolves an interaction by id — the single writer for "this ask is no
  // longer waiting" across BOTH representations: removes it from the
  // session-level `pendingInteractions` map, and additionally nulls the live
  // `pendingInteraction` slot when that slot currently holds the same id.
  // Callers (the live card's submit, a carried PastTurn card's submit,
  // `interaction_resolved`/`interaction_expired` SSE frames, the reconcile
  // hook) call this instead of `clearPendingInteraction()` so a carried ask
  // never outlives its own resolution.
  resolveInteraction: (interactionId: string) => void;
  reset: () => void;
}

const _initial: Pick<
  MultiAgentState,
  "runId" | "turnKey" | "runResumed" | "status" | "lane" | "chat" | "todo" | "nodes" | "edges"
  | "reasoningBuffer" | "textBuffer" | "finalText" | "partialFindings"
  | "memoryLoaded" | "sessionContext" | "promotionTotals" | "cost" | "validationProgress" | "finalMarkdown" | "finalArtifactId" | "followUps" | "feedbackRating" | "proposedFixes" | "verifier" | "contractViolation" | "blockedAction" | "autoFixFailed" | "thread" | "sessionId" | "plannerFallback" | "planReused" | "selectedNodeId" | "activityLog" | "sseStatus" | "sfConnected" | "artifacts" | "readOnly"
  | "runStartedAt" | "runFinishedAt" | "escalationHint" | "changeset" | "pendingInteraction" | "pendingInteractions"
> = {
  runId: null,
  turnKey: null,
  runResumed: false,
  status: "idle",
  lane: null,
  escalationHint: null,
  changeset: null,
  pendingInteraction: null,
  pendingInteractions: {},
  chat: [],
  todo: [],
  nodes: {},
  edges: [],
  reasoningBuffer: {},
  textBuffer: {},
  finalText: "",
  partialFindings: [],
  memoryLoaded: null,
  sessionContext: null,
  promotionTotals: null,
  cost: null,
  validationProgress: null,
  finalMarkdown: "",
  finalArtifactId: null,
  followUps: [],
  feedbackRating: null,
  proposedFixes: [],
  verifier: null,
  contractViolation: null,
  blockedAction: null,
  autoFixFailed: null,
  thread: [],
  sessionId: null,
  plannerFallback: null,
  planReused: null,
  selectedNodeId: null,
  activityLog: [],
  sseStatus: "idle",
  sfConnected: true,
  artifacts: [],
  readOnly: false,
  runStartedAt: null,
  runFinishedAt: null,
};

// Local persistence — scoped to user + org. Stores only runId + sessionId;
// thread body is restored from GET /detail (session_runs) on hydrate.
const LEGACY_STORAGE_KEY = "ma:session";

export type PersistedMultiAgentSession = {
  runId: string | null;
  sessionId: string | null;
};

function resolveMultiAgentTenant(): { userId: string; orgId: string } | null {
  const state = useAuthStoreV1.getState();
  const userId = state.user?.id;
  const orgId = state.selectedOrg?.id;
  if (!userId || !orgId) return null;
  return { userId, orgId };
}

function multiAgentSessionStorageKey(userId: string, orgId: string): string {
  return `ma:session:${userId}:${orgId}`;
}

function readPersisted(): PersistedMultiAgentSession {
  const tenant = resolveMultiAgentTenant();
  if (!tenant) return { runId: null, sessionId: null };
  try {
    const key = multiAgentSessionStorageKey(tenant.userId, tenant.orgId);
    const raw = localStorage.getItem(key);
    if (!raw) return { runId: null, sessionId: null };
    const v = JSON.parse(raw);
    return {
      runId: typeof v.runId === "string" ? v.runId : null,
      sessionId: typeof v.sessionId === "string" ? v.sessionId : null,
    };
  } catch {
    return { runId: null, sessionId: null };
  }
}

export function readPersistedMultiAgentSession(): PersistedMultiAgentSession {
  return readPersisted();
}

function writePersisted(p: Partial<PersistedMultiAgentSession>): void {
  const tenant = resolveMultiAgentTenant();
  if (!tenant) return;
  try {
    const key = multiAgentSessionStorageKey(tenant.userId, tenant.orgId);
    const prev = readPersisted();
    localStorage.setItem(key, JSON.stringify({ ...prev, ...p }));
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch { /* quota / SSR safety */ }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    const tenant = resolveMultiAgentTenant();
    if (tenant) {
      localStorage.removeItem(multiAgentSessionStorageKey(tenant.userId, tenant.orgId));
    }
  } catch { /* noop */ }
}

// ── Sent-attachment thumbnail persistence (survive a page refresh) ───────────
// The chat/thread rehydrate from the backend on reload, which carries NO
// thumbnails — so a sent image/screenshot preview vanished on refresh. We keep a
// small, tenant-scoped map { runId → [downscaled thumbnails] } in localStorage and
// re-attach it during hydrateFromDetail. Thumbnails are downscaled (full
// screenshots would blow the ~5MB localStorage budget) and the map is bounded.
const _MA_ATTACHMENTS_MAX_RUNS = 15;

function multiAgentAttachmentsStorageKey(userId: string, orgId: string): string {
  return `ma:attachments:${userId}:${orgId}`;
}

/** Downscale an image data URL to a tiny thumbnail for durable storage. Returns
 *  the input unchanged for non-images or on any failure (best-effort). */
async function _downscaleDataUrl(dataUrl: string, maxPx = 200): Promise<string> {
  try {
    if (!dataUrl.startsWith("data:image/")) return dataUrl;
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = dataUrl;
    });
    const longest = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxPx / longest);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const cx = canvas.getContext("2d");
    if (!cx) return dataUrl;
    cx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return dataUrl;
  }
}

/** Persist a run's sent attachments (downscaled) so their bubble thumbnails
 *  survive a refresh. Fire-and-forget; failures are non-fatal.
 *
 *  Two-phase write:
 *  1. Synchronously write attachment metadata (id/name/kind, no thumbnail bytes)
 *     so a page refresh that happens before the canvas downscale finishes still
 *     shows attachment chips on reload.
 *  2. Async: downscale thumbnails, then overwrite with the richer record.
 */
export async function persistRunAttachments(
  runId: string,
  attachments: ChatMsgAttachment[],
): Promise<void> {
  const tenant = resolveMultiAgentTenant();
  if (!tenant || !runId || !attachments || attachments.length === 0) return;

  const writeToStorage = (entries: ChatMsgAttachment[]) => {
    try {
      const key = multiAgentAttachmentsStorageKey(tenant.userId, tenant.orgId);
      const raw = localStorage.getItem(key);
      const map: Record<string, ChatMsgAttachment[]> = raw ? JSON.parse(raw) : {};
      map[runId] = entries;
      const ids = Object.keys(map);
      if (ids.length > _MA_ATTACHMENTS_MAX_RUNS) {
        for (const old of ids.slice(0, ids.length - _MA_ATTACHMENTS_MAX_RUNS)) delete map[old];
      }
      localStorage.setItem(key, JSON.stringify(map));
    } catch { /* quota / SSR / parse error — non-fatal */ }
  };

  // Phase 1 — synchronous: write metadata only (no large thumbnail bytes).
  // This runs before any await, so a refresh in the next millisecond still
  // finds the attachment record and shows chips (without image previews).
  writeToStorage(attachments.map((a) => ({ id: a.id, name: a.name, kind: a.kind })));

  // Phase 2 — async: downscale thumbnails for durable image previews and
  // overwrite the metadata-only record with the richer version.
  try {
    const slim: ChatMsgAttachment[] = await Promise.all(
      attachments.map(async (a) => ({
        id: a.id,
        name: a.name,
        kind: a.kind,
        thumbnailDataUrl: a.thumbnailDataUrl
          ? await _downscaleDataUrl(a.thumbnailDataUrl)
          : undefined,
      })),
    );
    writeToStorage(slim);
  } catch {
    /* Phase 1 metadata record stays in localStorage — acceptable fallback */
  }
}

/** Read a run's persisted attachments (for hydrateFromDetail). */
function readRunAttachments(runId: string): ChatMsgAttachment[] | undefined {
  const tenant = resolveMultiAgentTenant();
  if (!tenant || !runId) return undefined;
  try {
    const raw = localStorage.getItem(
      multiAgentAttachmentsStorageKey(tenant.userId, tenant.orgId),
    );
    if (!raw) return undefined;
    const map = JSON.parse(raw) as Record<string, ChatMsgAttachment[]>;
    const a = map[runId];
    return Array.isArray(a) && a.length > 0 ? a : undefined;
  } catch {
    return undefined;
  }
}
// Session-first URL model: the URL carries ONLY ?session=<id> (the durable key);
// run ids stay internal to the store. A refresh restores the session and resolves
// its latest run. We also strip any stale ?run= so the two params can never
// disagree (?run= remains a read-only back-compat deep link on first load only).
// No-op on SSR.
function pushSessionToUrl(_sessionId: string | null): void {
  // Keep the URL CLEAN — dev-agent parity. dev-agent never exposes a run OR
  // session id in the address bar; the session is restored from localStorage
  // (writePersisted) on refresh, and ?run=/?session= stay read-only deep-links
  // consumed once on first load. So we only ever STRIP id params here, never
  // write them. (Param kept for call-site compatibility.)
  try {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has("run")) { url.searchParams.delete("run"); changed = true; }
    // `?session=` is owned by `useLaneV2Route` (it writes/removes it through the router); a raw replaceState here is
    // invisible to React Router and made the two disagree.
    if (changed) window.history.replaceState({}, "", url.toString());
  } catch { /* noop */ }
}

export const useMultiAgentStore = create<MultiAgentState>((set, get) => ({
  ..._initial,
  history: [],
  thread: [],
  sessionId: null,

  startRun: (runId, intent, attachments, resumed) => {
    // The run id NEVER goes in the URL. We still key the in-memory session +
    // localStorage off runId when no real session exists yet (so a refresh can
    // restore from persisted state), but the URL only ever carries a real
    // session id distinct from the run — never the run id (a brand-new run
    // keeps a clean URL).
    const sid = get().sessionId || runId;
    pushSessionToUrl(sid === runId ? null : sid);
    writePersisted({ runId, sessionId: sid });
    set((s) => ({
      ..._initial,
      history: s.history,
      thread: s.thread,
      sessionId: sid,
      // PERSIST across the run transition so the right panel / Workspace don't
      // flash empty during a new run's initiation (same intent as reset(),
      // which also keeps these). Run-specific state (nodes, cost, verifier,
      // timeline, status) still resets — the new run repopulates it. Artifacts,
      // session context and loaded memory carry over until new data replaces them.
      artifacts: s.artifacts,
      sessionContext: s.sessionContext,
      memoryLoaded: s.memoryLoaded,
      // Session-level "still waiting" projection — survives the per-run reset
      // exactly like artifacts/sessionContext above. Only the LIVE single-slot
      // `pendingInteraction` (reset by the `..._initial` spread) is per-run.
      pendingInteractions: s.pendingInteractions,
      runId,
      // A fresh identity for THIS turn — deliberately independent of `runId`.
      // A resumed run (backend continues a pending-clarification run under
      // the SAME run_id) can hand back a `runId` equal to the turn that was
      // just snapshotted into `thread` by the caller. Since `turnKey` is
      // always new here, pushTurnToThread's dedup can't mistake this new
      // turn for that old one, even though their `runId`s collide.
      turnKey: crypto.randomUUID(),
      runResumed: resumed === true,
      status: "starting",
      runStartedAt: Date.now(),
      runFinishedAt: null,
      // When attachments aren't passed (hydration path — page refresh of a
      // running run), restore from the localStorage record written by
      // persistRunAttachments so the bubble thumbnails survive the refresh.
      // An explicit [] from a no-attachment send is preserved as-is.
      chat: [{ id: crypto.randomUUID(), role: "user", text: intent, attachments: attachments ?? readRunAttachments(runId) }],
    }));
  },

  setStatus: (status) => set({ status }),
  setLane: (lane) => set({ lane }),
  setEscalationHint: (escalationHint) => set({ escalationHint }),
  setBlockedAction: (blockedAction) => set({ blockedAction }),
  setSfConnected: (sfConnected) => set({ sfConnected }),

  appendUserMessage: (text) =>
    set((s) => ({
      chat: [...s.chat, { id: crypto.randomUUID(), role: "user", text }],
    })),

  appendReasoning: (nodeId, text) =>
    set((s) => ({
      reasoningBuffer: {
        ...s.reasoningBuffer,
        [nodeId]: (s.reasoningBuffer[nodeId] || "") + text,
      },
    })),

  endReasoning: (nodeId) =>
    set((s) => {
      const reasoning = s.reasoningBuffer[nodeId] || "";
      if (!reasoning.trim()) return {};
      const { [nodeId]: _, ...rest } = s.reasoningBuffer;
      return {
        reasoningBuffer: rest,
        chat: [
          ...s.chat,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            text: "",
            reasoning,
          },
        ],
      };
    }),

  appendText: (nodeId, text) =>
    set((s) => ({
      textBuffer: { ...s.textBuffer, [nodeId]: (s.textBuffer[nodeId] || "") + text },
      finalText: nodeId === "synthesizer" ? s.finalText + text : s.finalText,
    })),

  endText: (nodeId) =>
    set((s) => {
      const text = s.textBuffer[nodeId] || "";
      if (!text.trim()) return {};
      const { [nodeId]: _, ...rest } = s.textBuffer;
      if (nodeId !== "synthesizer") {
        return {
          textBuffer: rest,
        };
      }
      return {
        textBuffer: rest,
        chat: [
          ...s.chat,
          { id: crypto.randomUUID(), role: "assistant", text },
        ],
        // synthesizer's terminal text IS the final report
        finalMarkdown: sanitizeArtifactMarkdown(text),
      };
    }),

  setPlan: (nodes, edges, todo) => {
    const nodesMap: Record<string, AgentNodeData> = {};
    for (const n of nodes) nodesMap[n.id] = n;
    // plan_gate now auto-approves; we go straight from planning to running.
    // Keep status as planning here — `plan_approved` SSE flips it to running.
    set({ nodes: nodesMap, edges, todo, status: "planning" });
  },

  setNodeStatus: (nodeId, status, toolInFlight) =>
    set((s) => {
      const prev = s.nodes[nodeId];
      if (!prev) return {};
      return {
        nodes: {
          ...s.nodes,
          [nodeId]: { ...prev, status, toolInFlight },
        },
        todo: s.todo.map((t) =>
          t.id === nodeId
            ? {
                ...t,
                status:
                  status === "done"
                    ? "done"
                    : status === "failed"
                      ? "failed"
                      : status === "running" || status === "tool_calling"
                        ? "running"
                        : t.status,
              }
            : t,
        ),
      };
    }),

  setNodeOutput: (nodeId, output) =>
    set((s) => {
      const prev = s.nodes[nodeId];
      if (!prev) return {};
      return { nodes: { ...s.nodes, [nodeId]: { ...prev, output } } };
    }),

  activateEdge: (from, to) =>
    set((s) => ({
      edges: s.edges.map((e) =>
        e.from === from && e.to === to ? { ...e, active: true } : e,
      ),
    })),

  approvePlan: () => set({ status: "running" }),

  addPartialFinding: (nodeId, role, summary) =>
    set((s) => ({
      partialFindings: [...s.partialFindings, { nodeId, role, summary }],
    })),

  setMemoryLoaded: (m) => set({ memoryLoaded: m }),
  setSessionContext: (m) => set({ sessionContext: m }),
  setPromotionTotals: (t) => set({ promotionTotals: t }),
  setCost: (c) => set({ cost: c }),
  setValidationProgress: (p) => set({ validationProgress: p }),
  setFinalArtifact: (artifactId) => set({ finalArtifactId: artifactId }),
  setFollowUps: (f) => set({ followUps: Array.isArray(f) ? f : [] }),
  setHistory: (h) => set({ history: h }),
  setFeedback: (rating) => set({ feedbackRating: rating }),
  setProposedFixes: (fixes) => set({ proposedFixes: fixes }),
  setVerifier: (v) => set({ verifier: v }),
  setContractViolation: (v) => set({ contractViolation: v }),
  setAutoFixFailed: (v) => set({ autoFixFailed: v }),
  // Mirror sessionId + runId to tenant-scoped localStorage.
  _persistMA: () => {
    const s = get();
    writePersisted({
      runId: s.runId,
      sessionId: s.sessionId,
    });
  },

  pushTurnToThread: () => set((s) => {
    const userMsg = s.chat.find((m) => m.role === "user");
    const intent = userMsg?.text || "";
    const attachments = userMsg?.attachments;
    const md = sanitizeArtifactMarkdown(s.finalMarkdown).trim();
    // Capture the activity timeline (last 50 entries) and reasoning
    // tail so prior turns are visually rich, not just an answer.
    const activity = s.activityLog.slice(-50).map((e) => ({
      id: e.id, kind: e.kind, text: e.text, tone: e.tone,
      tool: e.tool, argsSummary: e.argsSummary, durationMs: e.durationMs,
    }));
    const reasoning = Object.values(s.reasoningBuffer)
      .filter(Boolean).join("\n\n").trim().slice(-1500);
    // Only THIS run's artifacts (tagged by runId) — not the whole accumulated
    // set — so the snapshotted turn renders its own diff/code/evidence, not the
    // entire session's artifacts piling onto every turn.
    const artifactIds = s.artifacts
      .filter((a) => (a.runId ? a.runId === s.runId : true))
      .map((a) => a.id);
    // Carry the outgoing run's still-open question (F3 fix) — the fact
    // already lives in `pendingInteractions` (set by `setPendingInteraction`),
    // so the turn only needs the id. `s.pendingInteraction` is this run's own
    // live slot: present here means it's still unanswered at send time.
    const pendingInteractionId = s.pendingInteraction ? s.pendingInteraction.interaction_id : null;
    // Preserve the turn if it produced ANY output — answer prose, artifacts
    // (code/diffs), an activity timeline, OR a still-open question. Previously
    // this bailed whenever the answer markdown was empty, which made
    // artifact-only turns (e.g. a refactor that produced a code diff but no
    // prose) DISAPPEAR on the next send; the pendingInteraction clause guards
    // the same disappearance for a turn whose only "output" so far is a
    // still-unanswered approve_changeset/clarify ask.
    if (
      !s.runId ||
      !intent ||
      (!md && artifactIds.length === 0 && activity.length === 0 && !pendingInteractionId)
    ) return s;
    // Fall back to runId only as a defensive net (turnKey is always set by
    // startRun/hydrateFromDetail) — never the primary identity, since a
    // resumed run can share its runId with the PRIOR turn (see turnKey doc).
    const turnKey = s.turnKey || s.runId;
    const nextTurn = {
      runId: s.runId,
      turnKey,
      intent, attachments, finalMarkdown: md, lane: s.lane,
      activity, reasoning, artifactIds,
      pendingInteractionId,
    };
    // Keep chronological order: if this TURN is ALREADY in the thread (e.g.
    // restored mid-conversation), update it in place rather than dropping it
    // and re-appending — re-appending would move a middle turn to the end and
    // render the follow-up out of order. Only a genuinely new turn appends.
    // Matched on turnKey, NOT runId — a resumed run can hand back the SAME
    // runId as the turn that was pushed for the PREVIOUS send, and matching
    // on runId there would silently overwrite that earlier turn instead of
    // appending this new one (see MultiAgentState.turnKey).
    const existingIdx = s.thread.findIndex((turn) => turn.turnKey === turnKey);
    if (existingIdx >= 0) {
      const thread = s.thread.slice();
      thread[existingIdx] = nextTurn;
      return { thread };
    }
    return { thread: [...s.thread, nextTurn] };
  }),
  clearThread: () => {
    clearPersisted();
    pushSessionToUrl(null);
    // FULL blank-session reset (keep only the run-list history). Previously this
    // cleared just thread+sessionId, so the last answer (finalMarkdown), its
    // artifacts, diffs and verdicts lingered on screen after "New session" — it
    // looked like the current session until the next send finally wiped them.
    set({ ..._initial, history: get().history });
  },
  setPlannerFallback: (f) => set({ plannerFallback: f }),
  setPlanReused: (p) => set({ planReused: p }),
  selectNode: (nodeId) => set({ selectedNodeId: nodeId }),

  appendActivity: (entry) =>
    set((s) => {
      // Cap the log at 500 entries so very long runs don't blow memory.
      const next = s.activityLog.length >= 500
        ? [...s.activityLog.slice(-499), entry]
        : [...s.activityLog, entry];
      return { activityLog: next };
    }),
  setSseStatus: (s) => set({ sseStatus: s }),
  addArtifact: (a) =>
    set((st) => ({
      artifacts: st.artifacts.some((x) => x.id === a.id)
        ? st.artifacts
        // Tag the artifact with the run that produced it (the live run) so each
        // turn renders its OWN artifacts. Falls back to a.runId if the caller set it.
        : [...st.artifacts, { ...a, runId: a.runId ?? st.runId ?? undefined, name: sanitizeArtifactLabel(a.name, a.id.slice(0, 8)) }],
    })),
  removeArtifact: (id) =>
    set((st) => ({ artifacts: st.artifacts.filter((a) => a.id !== id) })),

  // Called on normal terminal events (run_completed / run_failed). Same
  // shape as markCancelled but keeps the run "done"/"failed" rather than
  // "cancelled", and doesn't downgrade the sseStatus to "disconnected"
  // — that pill is hidden once the run is terminal anyway.
  finalizeRun: (s) =>
    set((st) => {
      const nodes: Record<string, AgentNodeData> = {};
      for (const [id, n] of Object.entries(st.nodes)) {
        const wasInFlight = n.status === "running" || n.status === "tool_calling";
        nodes[id] = {
          ...n,
          // Successful end → roll forward to "done"; failed → mark "failed"
          status: wasInFlight ? (s === "done" ? "done" : "failed") : n.status,
          toolInFlight: undefined,
        };
      }
      return { status: s, nodes, runFinishedAt: st.runFinishedAt ?? Date.now() };
    }),

  // Optimistic stop: invoked when the user clicks Stop OR the run_cancelled
  // SSE event arrives. Clears every in-flight indicator so the UI freezes
  // immediately rather than waiting for in-flight tool calls to finish.
  markCancelled: () =>
    set((st) => {
      const nodes: Record<string, AgentNodeData> = {};
      for (const [id, n] of Object.entries(st.nodes)) {
        const wasInFlight = n.status === "running" || n.status === "tool_calling";
        nodes[id] = {
          ...n,
          status: wasInFlight ? "skipped" : n.status,
          toolInFlight: undefined,
        };
      }
      return {
        status: "cancelled",
        nodes,
        sseStatus: "disconnected",
        runFinishedAt: st.runFinishedAt ?? Date.now(),
      };
    }),

  hydrateFromDetail: (d, options) => {
    const nodesMap: Record<string, AgentNodeData> = {};
    for (const n of d.nodes) {
      nodesMap[n.id] = {
        id: n.id,
        role: n.role,
        task: n.task,
        status: n.status as NodeStatus,
        isSupervisor: n.isSupervisor,
        toolInFlight: n.toolInFlight ?? undefined,
      };
    }
    const edges: PlanEdge[] = (d.edges || []).map(([from, to]) => ({ from, to }));
    // Past-run hydrate: keep only the user's original intent in chat.
    // The synth/worker text buffers come back as "assistant" messages from
    // the /detail endpoint but they're already in finalMarkdown — rendering
    // them as chat bubbles in the past-run view duplicates content and
    // confuses the user-asked / agent-answer header in ChatPanel.
    // Re-attach the ACTIVE run's sent thumbnails to its user bubble (the first
    // user message), so an image/screenshot preview survives a refresh.
    const activeRunAttachments = readRunAttachments(d.run.id);
    const chat: ChatMessage[] = d.chat
      .filter((m) => m.role === "user")
      .map((m, i) => ({
        id: crypto.randomUUID(),
        role: m.role,
        text: cleanDisplayIntent(m.text || ""),
        reasoning: m.reasoning,
        attachments: i === 0 ? activeRunAttachments : undefined,
      }));
    const todo: TodoItem[] = d.todo.map((t) => ({
      id: t.id,
      label: t.label,
      status: t.status as TodoItem["status"],
    }));
    const restoredThread = (d.session_runs || [])
      .filter((r) => r.runId !== d.run.id && r.intent && r.finalMarkdown)
      // Render in real conversation order — session_runs arrives in no
      // guaranteed order, so sort by start time (oldest first). Without this
      // a refreshed/rehydrated session can show follow-ups above the turn
      // they came after.
      .slice()
      .sort((a, b) => (a.startedAt || "").localeCompare(b.startedAt || ""))
      .map((r) => ({
        runId: r.runId,
        // No client-side turnKey survives a reload for a past backend turn —
        // fall back to its runId, same as pre-turnKey behavior. This is safe
        // here: the resumed-run collision only bites the CURRENT turn vs. the
        // one just snapshotted client-side in the same session, not distinct
        // already-finished backend rows.
        turnKey: r.runId,
        intent: cleanDisplayIntent(r.intent),
        // Re-attach this past turn's sent thumbnails from localStorage — the
        // backend session_runs carry no attachments, so without this the image/
        // screenshot previews vanish on refresh.
        attachments: readRunAttachments(r.runId),
        finalMarkdown: sanitizeArtifactMarkdown(r.finalMarkdown),
        lane: r.lane,
        activity: [],
        reasoning: "",
        // Restore this past run's artifact ids (from the backend session_runs)
        // so its diff/code/citation cards re-render on reload instead of
        // collapsing to plain text.
        artifactIds: (r.artifacts ?? []).map((a) => a.id),
        // The backend's `session_runs` rows carry no interaction state — a
        // reloaded/restored past turn's carried-ask fact only survives via
        // `options.preserveSession` merging in the LOCAL thread entry below
        // (which still has its real pendingInteractionId), never from here.
        pendingInteractionId: null,
      }));
    const isTerminal = ["done", "failed", "cancelled"].includes(d.run.status);
    set((s) => ({
      ..._initial,
      // Preserve cross-run conversation state for follow-up navigation.
      // If a refresh left localStorage stale/empty, backfill from the
      // durable session_runs returned by the backend. When local turns
      // exist, keep their richer activity/reasoning snapshots.
      history: s.history,
      thread: options?.preserveSession
        ? (() => {
            const localByRun = new Map(s.thread.map((turn) => [turn.runId, turn]));
            const restoredIds = new Set(restoredThread.map((turn) => turn.runId));
            return [
              ...restoredThread.map((turn) => localByRun.get(turn.runId) || turn),
              ...s.thread.filter((turn) => !restoredIds.has(turn.runId)),
            ];
          })()
        : restoredThread,
      sessionId: options?.preserveSession
        ? s.sessionId
        : d.run.session_id || d.session_id || s.sessionId || d.run.id,
      // Session-level carried-ask map — preserved across an in-session
      // hydrate (mirrors `thread` above) so a still-open ask carried onto an
      // earlier turn survives a mid-conversation `/detail` refresh; reset (to
      // `_initial`'s `{}`) when switching to a genuinely different run/session,
      // same as `restoredThread` resetting `thread` in that case.
      pendingInteractions: options?.preserveSession ? s.pendingInteractions : {},
      runId: d.run.id,
      // Mirrors runId here (no client turnKey survives a reload) — matches
      // restoredThread's fallback above so a later pushTurnToThread() for
      // this SAME hydrated run still merges into its restored thread entry
      // in place, instead of duplicating it.
      turnKey: d.run.id,
      // Restore run timing from the durable detail so the elapsed timer shows
      // the real run duration for a loaded/past run (and keeps counting if it's
      // still in flight).
      runStartedAt: d.run.started_at ? Date.parse(d.run.started_at) : null,
      runFinishedAt: d.run.finished_at ? Date.parse(d.run.finished_at) : null,
      // Chat restores are resumable sessions by default. The separate
      // runs page is the read-only detail surface; only callers that
      // explicitly ask for viewOnly should hide the composer.
      readOnly: Boolean(options?.viewOnly && isTerminal),
      status: d.run.status === "done" ? "done" : d.run.status === "failed" ? "failed" : (d.run.status === "cancelled" ? "cancelled" : "running"),
      lane: d.run.lane,
      nodes: nodesMap,
      edges,
      chat,
      todo,
      partialFindings: d.partial_findings,
      proposedFixes: d.proposed_fixes,
      // Don't let a terminal /detail with an EMPTY final answer wipe the answer
      // we already streamed/loaded for THIS run — the just-completed detail can
      // momentarily lack final_markdown (e.g. synthesis persisted as an artifact
      // before the run row's final text is written), which made the code +
      // contract "disappear" a couple minutes in. Switching to a DIFFERENT run
      // uses that run's detail as-is.
      finalMarkdown:
        sanitizeArtifactMarkdown(d.final_markdown) ||
        (d.run.id === s.runId ? s.finalMarkdown : ""),
      finalArtifactId: d.final_artifact_id || (d.run.id === s.runId ? s.finalArtifactId : null),
      followUps: Array.isArray(d.follow_ups) ? d.follow_ups : [],
      cost: d.cost,
      memoryLoaded: d.memory_loaded,
      sessionContext: d.session_context,
      promotionTotals: d.promotion_totals,
      // Restore the run's artifacts so `[label](artifact://id)` citations in
      // finalMarkdown resolve to chips. Without this, hydrated/past runs have
      // an empty artifact set and every citation renders as a broken link.
      artifacts: (() => {
        const mapArtifact = (a: {
          id: string; type: string; name: string; tool_name: string;
          metadata: Record<string, unknown> | null; created_at: string | null;
        }, runId: string) => {
          const meta = (a.metadata ?? {}) as Record<string, unknown>;
          const size = meta.byte_size ?? meta.size_bytes ?? meta.bytes;
          // Typed intake metadata lives under metadata.dependencies.intake (DB)
          // or metadata.intake (SSE). Present only for uploaded attachments.
          const deps = (meta.dependencies ?? {}) as Record<string, unknown>;
          const intake = (meta.intake ?? deps.intake ?? null) as Record<string, unknown> | null;
          return {
            id: a.id,
            type: a.type,
            name: sanitizeArtifactLabel(a.name, a.id.slice(0, 8)),
            toolName: a.tool_name,
            byteSize: typeof size === "number" ? size : 0,
            createdAt: a.created_at ? Date.parse(a.created_at) : 0,
            runId,
            intake,
          };
        };
        const incoming = (d.artifacts ?? []).map((a) => mapArtifact(a, d.run.id));
        // ALSO include every PAST run's artifacts (backend now returns them per
        // session_run), TAGGED with that run's id so each turn renders its OWN
        // artifacts instead of the whole session's set piling up at the end.
        const pastArtifacts = (d.session_runs ?? []).flatMap((r) => (r.artifacts ?? []).map((a) => mapArtifact(a, r.runId)));
        // NEVER lose artifacts: union by id (current run wins on conflict).
        if (options?.viewOnly) {
          const v = new Map(pastArtifacts.map((a) => [a.id, a]));
          for (const a of incoming) v.set(a.id, a);
          return Array.from(v.values());
        }
        const byId = new Map(get().artifacts.map((a) => [a.id, a]));
        for (const a of pastArtifacts) byId.set(a.id, a);
        for (const a of incoming) byId.set(a.id, a);
        return Array.from(byId.values());
      })(),
    }));
  },

  // Changeset HITL reducer actions (GUI_SYNC_SPEC_2026-08-29 P0-3, §3 state
  // machine). Each is a narrow, named transition — see the interface doc.
  changesetReady: (runId) =>
    set({
      changeset: {
        runId, phase: "ready", applyLogId: null, precheck: null,
        overrideReason: null, phases: [], applyResult: null, revertResult: null,
        error: null,
      },
    }),
  changesetPrechecking: () =>
    set((s) => (s.changeset ? { changeset: { ...s.changeset, phase: "prechecking", error: null } } : {})),
  changesetPrecheckResult: (precheck) =>
    set((s) => (s.changeset ? { changeset: { ...s.changeset, phase: "precheck_result", precheck } } : {})),
  changesetSetOverrideReason: (overrideReason) =>
    set((s) => (s.changeset ? { changeset: { ...s.changeset, overrideReason } } : {})),
  changesetApplying: (applyLogId) =>
    set((s) => (s.changeset ? { changeset: { ...s.changeset, phase: "applying", applyLogId, phases: [] } } : {})),
  changesetReverting: () =>
    set((s) => (s.changeset ? { changeset: { ...s.changeset, phase: "reverting", phases: [] } } : {})),
  // The started frame carries no `status` — the reducer stamps "running" so the
  // 4-step progress list has something to render before the finished frame lands.
  changesetPhaseStarted: ({ ord, phase }) =>
    set((s) => {
      if (!s.changeset) return {};
      const next = s.changeset.phases.filter((p) => p.ord !== ord);
      next.push({ ord, phase, status: "running" });
      next.sort((a, b) => a.ord - b.ord);
      return { changeset: { ...s.changeset, phases: next } };
    }),
  changesetPhaseFinished: ({ ord, phase, status, counts, error }) =>
    set((s) => {
      if (!s.changeset) return {};
      const next = s.changeset.phases.filter((p) => p.ord !== ord);
      next.push({ ord, phase, status, counts, error });
      next.sort((a, b) => a.ord - b.ord);
      return { changeset: { ...s.changeset, phases: next } };
    }),
  changesetApplied: ({ applyLogId, phases, ...applyResult }) =>
    set((s) =>
      s.changeset
        ? {
            changeset: {
              ...s.changeset,
              phase: applyResult.outcome === "succeeded" ? "applied" : "failed",
              applyLogId,
              phases: phases ?? s.changeset.phases,
              applyResult,
            },
          }
        : {},
    ),
  changesetReverted: ({ applyLogId, ...revertResult }) =>
    set((s) =>
      s.changeset
        ? {
            changeset: {
              ...s.changeset,
              phase: "reverted",
              applyLogId,
              revertResult,
            },
          }
        : {},
    ),
  changesetError: (message) =>
    set((s) => (s.changeset ? { changeset: { ...s.changeset, error: message } } : {})),
  resetChangeset: () => set({ changeset: null }),

  setPendingInteraction: (i) =>
    set((s) => {
      if (!i || !i.interaction_id) return { pendingInteraction: i };
      // Sole writer of the "still waiting" fact: the live slot AND its
      // session-level entry are written together here so they can never
      // drift — a carried PastTurn card later reads ONLY the map entry.
      return {
        pendingInteraction: i,
        pendingInteractions: {
          ...s.pendingInteractions,
          [i.interaction_id]: { ...i, runId: s.runId ?? "" },
        },
      };
    }),
  clearPendingInteraction: () => set({ pendingInteraction: null }),
  resolveInteraction: (interactionId) =>
    set((s) => {
      if (!interactionId) return {};
      if (!(interactionId in s.pendingInteractions) && s.pendingInteraction?.interaction_id !== interactionId) {
        return {};
      }
      const { [interactionId]: _removed, ...restMap } = s.pendingInteractions;
      return {
        pendingInteractions: restMap,
        pendingInteraction:
          s.pendingInteraction?.interaction_id === interactionId ? null : s.pendingInteraction,
      };
    }),

  // reset() keeps history (drawer cache) AND the conversation thread
  // AND the sessionId — refine() calls reset() between turns and we
  // want previous Q&As + the session to stay coherent. Use clearThread()
  // for a true new chat (also clears sessionId).
  reset: () => set({
    ..._initial,
    history: get().history,
    thread: get().thread,
    sessionId: get().sessionId,
    // PERSIST artifacts across runs (code, diffs, exports). reset() runs before
    // every new send; wiping artifacts here is what made the diffs disappear and
    // the Workspace empty between turns. They now persist until the user clears
    // a panel or switches tenant/session (resetMultiAgentForTenantChange →
    // clearThread also drops them). Prior-turn diffs resolve by artifact id.
    artifacts: get().artifacts,
    // PRESERVE the just-finished run's answer + verdicts. reset() is called
    // BEFORE the async /detail hydrate (MultiAgentRoute) and before startRun on
    // a follow-up; wiping finalMarkdown here is what made a shown answer (and its
    // code/contract) DISAPPEAR during that window. startRun() spreads _initial
    // and does NOT re-add these, so the NEW run still starts clean/streaming.
    finalMarkdown: get().finalMarkdown,
    finalArtifactId: get().finalArtifactId,
    proposedFixes: get().proposedFixes,
    contractViolation: get().contractViolation,
    verifier: get().verifier,
    autoFixFailed: get().autoFixFailed,
    // PERSIST the session-level carried-ask map across the reset() that runs
    // before every new send (right after pushTurnToThread snapshots the
    // outgoing turn) — mirrors artifacts/finalMarkdown above. Without this the
    // entry pushTurnToThread just referenced by id would vanish from under
    // the PastTurn that carries it.
    pendingInteractions: get().pendingInteractions,
  }),
}));

/** Clear persisted session + in-memory run state (org switch, logout, auth failure).
 *  Also drops `history` (the prior tenant's run list) so another user's runs can
 *  never flash on screen before the new tenant's query refetches. */
export function resetMultiAgentForTenantChange(): void {
  const { clearThread, reset, setHistory } = useMultiAgentStore.getState();
  clearThread();
  reset();
  setHistory([]);
}

// Auto-persist whenever the conversation state changes. Subscribing
// once at module load keeps every setter (pushTurnToThread, startRun,
// pushTurnRaw refine flows, etc.) honest without us threading
// writePersisted() through each one.
if (typeof window !== "undefined") {
  let lastKey = "";
  useMultiAgentStore.subscribe((s) => {
    const key = `${s.runId}|${s.sessionId}`;
    if (key === lastKey) return;
    lastKey = key;
    writePersisted({
      runId: s.runId,
      sessionId: s.sessionId,
    });
    // Session-first: reflect ONLY a durable session id in the URL — never the
    // run id. When sessionId is just the runId fallback (no real session yet),
    // keep the URL clean; restore still works via persisted runId in localStorage.
    pushSessionToUrl(s.sessionId && s.sessionId !== s.runId ? s.sessionId : null);
  });
}

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Registered here, at module scope, rather than in a `useEffect` on the Weave
// page. The effect only ran while that page was mounted, so switching orgs from
// anywhere else left this store holding the previous organization's session,
// thread and run history — which then surfaced the next time Weave was opened.
registerOrgScopedReset('multiAgentStore', resetMultiAgentForTenantChange);
